/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Viacheslav Smalenski
 * @version 1.29.0
 * @since 1.19.0
 */

import { memo, useEffect, useMemo, useState } from 'react';
import type { FC, ReactElement } from 'react';

import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';

import { TOverflow } from '@/main/types/globalTypes';
import { MultiSelect } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import {
    IMSOptionItemProps,
    IMultiSelectProps,
    MultiSelectItemProps,
} from '@components/common/MultiSelect/MultiSelect';
import { IOptionItemProps } from '@components/common/Select/Select';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';

import { useTableFilterApi, useTableFilterSuggestApi } from '../../api';
import { useTableFilter } from './hooks/useTableFilter';

export interface ITableFilterMultiSelect extends Partial<IMultiSelectProps> {
    name: string;
    searchInputMaxLength?: number;
    tooltipOverflow?: TOverflow;
    tooltipMaxHeight?: string;
    renderOptions?: (props: MultiSelectItemProps) => ReactElement;
    searchInputTooltipMsgCallback?: (searchValue: string) => IMultiSelectProps['searchInputTooltipMsg'];
    searchInputTypeCallback?: (searchValue: string) => IMultiSelectProps['searchInputType'];
}

const utoa = (str: string) => {
    return window.btoa(unescape(encodeURIComponent(str)));
};

const atou = (str: string) => {
    try {
        return decodeURIComponent(escape(window.atob(str)));
    } catch (e) {
        return str;
    }
};

const TableFilterMultiSelect: FC<ITableFilterMultiSelect> = ({
    name,
    searchInputMaxLength,
    tooltipOverflow,
    tooltipMaxHeight,
    searchInputTooltipMsgCallback,
    searchInputTypeCallback,
    ...props
}) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const [search, setSearch] = useState('');
    const [searchInputTooltipMsg, setSearchInputTooltipMsg] = useState<IMultiSelectProps['searchInputTooltipMsg']>();
    const [searchInputType, setSearchInputType] = useState<IMultiSelectProps['searchInputType']>();

    const [selectedOptions, setSelectedOptions] = useState<IOptionItemProps[]>([]);

    /* API */

    const apiFilter = useTableFilterApi();
    const apiSuggest = useTableFilterSuggestApi(true);

    const { facets, filters, selectedFilter } = useTableFilter(name);

    const isSuggest = !facets;

    /* set selected options */
    useEffect(() => {
        setSelectedOptions(
            selectedFilter?.value.map((value: string | number) => {
                const decodedValue = atou(value?.toString());
                return { label: decodedValue, value: decodedValue };
            }),
        );
    }, [selectedFilter]);

    const options: IMSOptionItemProps[] = useMemo(
        () =>
            (facets || apiSuggest.result).map((item: IMSOptionItemProps) => ({ label: item.toString(), value: item })),
        [facets, apiSuggest.result],
    );

    const onSelect = (item: IOptionItemProps[]) => {
        if (!isEqual(item, selectedOptions)) {
            setSelectedOptions(item);

            const value = item?.map((item) => utoa(item.value.toString()));
            const fieldFilter = { [name]: value.length ? { value } : undefined };

            apiFilter.filter({ ...filters, ...fieldFilter });
        }
        onCloseHandler();
    };

    const onSearchHandler = (value: string) => {
        /* Переопределение фильтрации */
        const tooltipMsg = searchInputTooltipMsgCallback?.(value);
        const type = searchInputTypeCallback?.(value);

        setSearchInputTooltipMsg(tooltipMsg);
        setSearchInputType(type);

        /* Фильтрация на сервере */
        if (isSuggest && type !== EInputKind.error) {
            setSearch(value);

            /* Максимальная длина строки в поиске */
            let searchValue = value;
            if (searchInputMaxLength && searchValue.length > searchInputMaxLength) {
                searchValue = searchValue.substring(0, searchInputMaxLength);
            }
            apiSuggest.getSuggest(name, searchValue);
        }
    };

    const onCloseHandler = () => {
        setSearchInputTooltipMsg(undefined);
        setSearchInputType(undefined);

        if (isSuggest) {
            setSearch('');
            apiSuggest.getSuggest(name, '');
        }
    };

    const multiSelectOptions = !search && !options?.length && selectedOptions ? selectedOptions : options;

    return (
        <MultiSelect
            // suggest фильтрация на беке
            filterInputHandlerCallback={!isSuggest ? props.filterInputHandlerCallback : undefined}
            input={props.input}
            isSuggest={isSuggest}
            kind={EInputKind.normal}
            options={multiSelectOptions}
            placeholder={props.placeholder}
            renderOptions={props.renderOptions}
            searchInputPlaceholder={props.searchInputPlaceholder}
            searchInputTooltipMsg={searchInputTooltipMsg}
            searchInputType={searchInputType}
            selectedOptions={selectedOptions || []}
            suggestIsLoading={apiSuggest.isMultiSelectFetching}
            tooltipMaxHeight={tooltipMaxHeight}
            tooltipOverflow={tooltipOverflow}
            translation={translationCommon}
            onClose={onCloseHandler}
            onSearchInputChange={debounce(onSearchHandler, 500)}
            onSelect={onSelect}
        />
    );
};

export default memo(TableFilterMultiSelect);
